import { formatNumber } from '@local_modules/flokit'
import { numberFixDecimals } from '@local_modules/generic/numbers/number-fix-decimals/index.js'
import { createGrid, themeQuartz } from 'ag-grid-community'
import { ascending, descending, rollup, sum } from 'd3'

/**
 * Shows the table as a PnL: with the accounts as lines, columns being 1 or multiple (for each tracking option); with validation confirmation for matching values
 * Can show either the Xero or DB(default) values
 *
 * @param {*} containerElementId
 * @param {*} data
 * @returns
 */
export function showFriendlyGrid (containerElementId, storedData) {
  const containerElement = document.getElementById(containerElementId)
  if (!containerElement) return

  const rowData = groupForRows(storedData)
  const columnData = makeColumns(storedData)
  const columnDefs = makeColumnDefs(columnData)

  // console.log('rowData', rowData)
  // console.log('columnData', columnData)
  // console.log('columnDefs', columnDefs)

  // Reset the container
  containerElement.innerHTML = ''

  const gridOptions = {
    rowData,
    columnDefs,
    defaultColDef: {
      onCellClicked: function (cellParams) {
        console.log('grid onCellClicked cellParams', cellParams)
      }
    },
    tooltipShowDelay: 100,
    tooltipInteraction: true,
    theme: themeQuartz
  }

  const gridApi = createGrid(containerElement, gridOptions)
  return gridApi
}

// Group by account: actual account
function groupForRows (storedData) {
  const rolledupData = rollup(storedData.rows,
    function (groupValues) {
      const totalAmountDb = numberFixDecimals(sum(groupValues, d => d.amountDb), 2)
      const rowIsMatching = groupValues.every(g => g.isMatching)
      return {
        name: groupValues[0].accountName || groupValues[0].xeroAccountId,
        groupValues,
        amountXero: numberFixDecimals(sum(groupValues, d => d.amountXero), 2),
        amountDb: totalAmountDb,
        isCredit: totalAmountDb > 0,
        isFx: groupValues[0].isKnownIssue || false,
        issue: groupValues[0].issue || null,
        isMatching: rowIsMatching
      }
    },
    function (value) {
      return value.accountName || value.xeroAccountId
    })

  const rowsData = [...rolledupData.values()]
  // console.log('rowsData', rowsData)

  rowsData.sort((a, b) => {
    // Fx at the bottom
    // Sorting the type of issue together
    // Credit at the top
    // Largest amount at the top
    return ascending(a.isFx, b.isFx) || ascending(a.issue, b.issue) || descending(a.isCredit, b.isCredit) || descending(Math.abs(a.amountDb), Math.abs(b.amountDb))
  })

  const totalAmountDb = numberFixDecimals(sum(storedData.rows, d => d.amountDb), 2)
  const totalAmountXero = numberFixDecimals(sum(storedData.rows, d => d.amountXero), 2)
  rowsData.push({
    name: 'Total Earnings',
    groupValues: storedData.rows,
    isTotal: true,
    amountDb: totalAmountDb,
    amountXero: totalAmountXero,
    variance: numberFixDecimals(totalAmountDb - totalAmountXero, 2),
    isMatching: totalAmountDb === totalAmountXero
  })

  return rowsData
}

function makeColumns (storedData) {
  // console.log('storedData', storedData)
  const rolledupData = rollup(storedData.rows,
    function (groupValues) {
      const trackingId = groupValues[0].tracking1 || null
      const trackingData = storedData.data?.trackingCategoriesAndOptions?.find(value => value.tracking_option_id === trackingId) || null
      const trackingName = trackingData?.tracking_option_name || 'Unassigned'
      return {
        groupValues,
        trackingCategoriesAndOptions: storedData.data?.trackingCategoriesAndOptions,
        trackingId: groupValues[0].tracking1 || null,
        name: trackingName,
        isUnassigned: groupValues[0].tracking1 === null,
        trackingData
      }
    },
    function (value) {
      return value.tracking1
    })

  const columnsData = [...rolledupData.values()]

  columnsData.sort((a, b) => {
    return ascending(a.isUnassigned, b.isUnassigned) || descending(a.name, b.name)
  })

  // console.log('columnsData', columnsData)

  return columnsData
}

function makeColumnDefs (columnsData) {
  const columnDefs = []
  columnDefs.push({
    headerName: 'Account',
    pinned: 'left',
    valueGetter: (params) => {
      return params.data.name
    }
  })

  columnsData.forEach(column => {
    columnDefs.push({
      headerName: column.name || '??',
      context: {
        column
      },
      type: 'numericColumn',
      valueGetter: cellValueGetter,
      valueFormatter,
      cellRenderer,
      tooltipValueGetter,
      onCellClicked: function (cellParams) {
        console.log('grid onCellClicked cellParams', cellParams)
        selectCellValues(cellParams, true)
      }
    })
  })

  columnDefs.push({
    headerName: 'Total',
    pinned: 'right',
    context: {
      isTotalColumn: true
    },
    type: 'numericColumn',
    valueGetter: cellValueGetter,
    valueFormatter,
    cellRenderer,
    tooltipValueGetter
  })
  return columnDefs
}

function tooltipValueGetter (cellParams) {
  const cellData = selectCellValues(cellParams)
  // console.log('tooltipValueGetter, cellValues', cellData)
  if (!cellData || cellData.cellValues?.length === 0) return null

  if (cellParams.data.isFx) {
    return `This line can not be compared directly: look at the total instead
    / 
    zenflow ${cellData.amountDb} vs. ${cellData.amountXero} in Xero`
  }

  if (Math.abs(cellData.variance) < 0.01) {
    return 'Zenflow matches Xero values'
  } else {
    return `zenflow ${cellData.amountDb} vs. ${cellData.amountXero} in Xero for a variance of ${cellData.variance} => It should be ok after the next data sync (See the Data Sync tab)`
  }
}

function selectCellValues (cellParams, debug = false) {
  // console.log('selectCellValues, cellParams', cellParams)
  const columnTrackingId = cellParams.colDef?.context?.column?.trackingId
  // console.log('selectCellValues, columnTrackingId', columnTrackingId)

  const groupValues = cellParams.data.groupValues
  const cellValues = groupValues?.filter(g => g.tracking1 === columnTrackingId || (cellParams.colDef?.context?.isTotalColumn))
  // console.log('selectCellValues, cellValues', cellValues)

  const cellData = {
    cellValues,
    amountDb: numberFixDecimals(sum(cellValues, d => d.amountDb), 2),
    amountXero: numberFixDecimals(sum(cellValues, d => d.amountXero), 2),
    variance: numberFixDecimals(sum(cellValues, d => d.amountDb) - sum(cellValues, d => d.amountXero), 2)
  }
  if (debug) {
    console.log('selectCellValues, cellData', cellData)
  }
  return cellData
}

function cellValueGetter (cellParams) {
  const cellData = selectCellValues(cellParams)
  if (!cellData || cellData.cellValues?.length === 0) return null
  return cellData.amountDb
}

function cellRenderer (cellParams) {
  const cellData = selectCellValues(cellParams)
  if (!cellData || cellData.cellValues?.length === 0) return null

  // const isOk = cellData.cellValues.every(g => g.isMatching) || false
  const isOk = Math.abs(cellData.variance) < 0.02

  let icon = ''
  if (isOk) {
    icon = '<i class="fa-solid fa-check" style="color: green;"></i>'
  } else if (cellParams.data?.isFx) {
    icon = '<i class="fa-solid fa-question" style="color: lightblue;"></i>'
  } else {
    icon = '<i class="fa-solid fa-question" style="color: orange;"></i>'
  }
  return `${cellParams.valueFormatted} ${icon}`
}

function valueFormatter (cellParams) {
  return formatNumber(cellParams.value, 2)
}
